import type { AdminStore } from '@/client/types/stores';
import { useStoreAnalyticsSeriesQuery, useStoreEngagementQuery } from '@/client/api/stores/parity.query';
import { Skeleton } from '@/components/ui/skeleton';

import { RangePicker, SectionCard, bucketFor, rangeFor, useRangeParam, TableSkeleton } from './shared';
import { SalesChart } from './SalesChart';

const FunnelStep: React.FC<{ label: string; value: number; rate?: number; last?: boolean }> = ({ label, value, rate, last }) => (
  <div className="flex flex-1 items-center gap-2">
    <div className="flex-1 rounded-lg bg-teal-50 px-3 py-2 text-center">
      <p className="text-xs uppercase tracking-wide text-teal-700">{label}</p>
      <p className="text-lg font-bold text-gray-900">{value.toLocaleString()}</p>
    </div>
    {!last && <span className="whitespace-nowrap text-xs text-gray-500">{rate != null ? `${rate}% →` : '→'}</span>}
  </div>
);

/**
 * Server-computed performance (`/analytics/series`, `/analytics/engagement`):
 * order metrics per bucket vs the previous period, and engagement across every
 * listing in the store (not a sample).
 */
export const PerformanceTab: React.FC<{ store: AdminStore }> = ({ store }) => {
  const [range, setRange] = useRangeParam('90d');
  const dates = rangeFor(range);
  const { data: series, isLoading } = useStoreAnalyticsSeriesQuery(store.storeid, { ...dates, bucket: bucketFor(range) });
  const { data: engagement } = useStoreEngagementQuery(store.storeid, { ...dates, top: 10 });

  return (
    <div className="space-y-4">
      <SalesChart
        series={series}
        loading={isLoading}
        description={series ? `Per ${series.bucket}, compared with the previous ${range.replace('d', ' days').replace('365 days', '12 months')}` : undefined}
        actions={<RangePicker value={range} onChange={setRange} />}
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <SectionCard title="Funnel" description="Impressions → listing views → paid orders">
          {engagement ? (
            <div className="flex items-center gap-2">
              <FunnelStep label="Impressions" value={engagement.funnel.impressions} rate={engagement.funnel.view_rate} />
              <FunnelStep label="Views" value={engagement.funnel.views} rate={engagement.funnel.order_rate} />
              <FunnelStep label="Paid orders" value={engagement.funnel.paid_orders} last />
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2">
                {[0, 1, 2].map((i) => <Skeleton key={i} className="h-[60px] flex-1 rounded-lg" />)}
              </div>
              <div className="mt-4 grid grid-cols-3 gap-2">
                {Array.from({ length: 6 }, (_, i) => <Skeleton key={i} className="h-[52px] rounded-md" />)}
              </div>
            </>
          )}
          {engagement && (
            <div className="mt-4 grid grid-cols-3 gap-2 text-center text-xs text-gray-600">
              {(['favorites', 'saves', 'shares', 'previews', 'comments', 'reviews'] as const).map((k) => (
                <div key={k} className="rounded-md bg-gray-50 py-2">
                  <p className="font-semibold text-gray-900">{engagement.totals[k].toLocaleString()}</p>
                  <p className="capitalize">{k}</p>
                </div>
              ))}
            </div>
          )}
        </SectionCard>

        <SectionCard title="Top listings" description={engagement ? `${engagement.listings_with_engagement} listings had engagement` : undefined}>
          {!engagement ? (
            <TableSkeleton rows={6} cols={4} />
          ) : engagement.top_listings.length === 0 ? (
            <p className="py-6 text-center text-sm text-gray-400">No engagement in this range.</p>
          ) : (
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-gray-400">
                  <th className="py-2 pr-4">Listing</th>
                  <th className="py-2 pr-4 text-right">Views</th>
                  <th className="py-2 pr-4 text-right">Impr.</th>
                  <th className="py-2 text-right">View rate</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {engagement.top_listings.map((l) => (
                  <tr key={l.eid}>
                    <td className="max-w-[200px] truncate py-2 pr-4 font-medium text-gray-900">{l.name}</td>
                    <td className="py-2 pr-4 text-right tabular-nums">{l.views.toLocaleString()}</td>
                    <td className="py-2 pr-4 text-right tabular-nums text-gray-500">{l.impressions.toLocaleString()}</td>
                    <td className="py-2 text-right tabular-nums">{l.view_rate}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </SectionCard>
      </div>
    </div>
  );
};
